/**
 * PATH: packages/email-hub/src/feature/components/MailboxReconnectBanner.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-MAILBOXRECONNECTBANNER-001
 * PURPOSE: Loud, dismissible banner that fires when the BACKEND has flagged a
 *          specific mailbox as needing reconnection — driven by the
 *          per-mailbox sync status (`is_active=false` or a
 *          `reconnect_required:` last_sync_error), NOT by the per-user OAuth
 *          token check that EmailConnectionBanner uses.
 * WHY: BUG-2026-06-10-EMAIL-SILENT-SYNC-DEATH — when an OAuth refresh hit
 *      `invalid_grant`, the backend flips `EmailAccount.is_active=False` and
 *      stops syncing that mailbox forever. The only FE signal was a quiet amber
 *      "Synced N days ago" chip; a SHARED mailbox dying never surfaced at all
 *      because the user's own token still looked connected. This banner makes
 *      a dead mailbox impossible to miss and routes to the reconnect flow.
 * FLOW:
 *   ┌───────────────────────────┐   ┌──────────────────────┐   ┌──────────┐
 *   │ syncStatus.mailboxes[]     │ → │ pick dead mailboxes  │ → │ <Banner> │
 *   │ (is_active/last_sync_error)│   │ + dismiss memory     │   │ or null  │
 *   └───────────────────────────┘   └──────────────────────┘   └──────────┘
 * DEPENDENCIES:
 *   - lucide-react: status icons
 *   - useEmails.types: EmailMailboxSyncStatus
 * RELATED:
 *   - feature/EmailLayout.tsx: renders this in the sticky banner stack
 *   - organisation/services/oauth_refresh_lock.py: backend that flips
 *     is_active=False + emits the matching NotificationService alert
 */
import { useMemo, useState } from 'react'
import { AlertTriangle, ExternalLink, X } from 'lucide-react'
import type { EmailMailboxSyncStatus } from '../hooks/useEmails.types'
import { selectReconnectMailboxes } from '../lib/reconnectMailboxes'

/** Stable dismissal key for the current dead-set, so dismissing hides THIS
 *  set but a NEW failure (different mailbox) re-surfaces. */
function dismissKeyFor(dead: EmailMailboxSyncStatus[]): string {
  const ids = dead.map((m) => m.id).sort((a, b) => a - b).join(',')
  return `qh-mailbox-reconnect-dismissed:${ids}`
}

interface Props {
  mailboxes: EmailMailboxSyncStatus[] | undefined
  /** `loginHint` (the dead mailbox's address) is forwarded so the OAuth
   *  picker pre-selects that exact identity — required to revive a SHARED
   *  mailbox, which can ONLY be reconnected by signing in AS itself. */
  onReconnectOutlook: (loginHint?: string) => void | Promise<void>
  onReconnectGmail: (loginHint?: string) => void | Promise<void>
  className?: string
}

function isShared(m: EmailMailboxSyncStatus): boolean {
  return m.account_type === 'shared'
}

export default function MailboxReconnectBanner({
  mailboxes,
  onReconnectOutlook,
  onReconnectGmail,
  className,
}: Props) {
  const dead = useMemo(() => selectReconnectMailboxes(mailboxes), [mailboxes])
  const dismissKey = dead.length ? dismissKeyFor(dead) : ''
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (!dismissKey) return false
    try {
      return sessionStorage.getItem(dismissKey) === '1'
    } catch {
      return false
    }
  })

  if (dead.length === 0 || dismissed) return null

  const anyShared = dead.some(isShared)
  const headline =
    dead.length === 1
      ? `${dead[0].email_address || 'A mailbox'} stopped syncing`
      : `${dead.length} mailboxes stopped syncing`

  // Targeted reconnect: pass the dead mailbox's OWN address as the login
  // hint so the provider picker lands on the right identity. This is what
  // makes a SHARED mailbox revivable — reconnecting the user's personal
  // account never flips the shared mailbox back to active
  // (BUG-2026-06-15-EMAIL-SHARED-MAILBOX-RECONNECT).
  const reconnect = (m: EmailMailboxSyncStatus) => {
    if (m.provider === 'gmail') void onReconnectGmail(m.email_address)
    else void onReconnectOutlook(m.email_address)
  }

  const dismiss = () => {
    setDismissed(true)
    try {
      if (dismissKey) sessionStorage.setItem(dismissKey, '1')
    } catch {
      /* no storage — banner just won't persist its dismissal */
    }
  }

  return (
    <div className={`px-4 pt-3 ${className ?? ''}`}>
      <div
        className="flex items-start gap-3 p-3 border rounded-lg border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 text-red-900 dark:text-red-100"
        role="alert"
        aria-live="assertive"
        data-testid="mailbox-reconnect-banner"
      >
        <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">
            {headline} - reconnect to resume email
          </div>
          <div className="text-xs opacity-80 mt-0.5">
            New mail will not arrive until reconnected. This usually happens
            when the provider sign-in expires or is revoked.
            {anyShared && (
              <>
                {' '}
                <strong>Shared</strong> mailboxes must be reconnected by signing
                in as that exact account in the provider picker.
              </>
            )}
          </div>
          {/* Per-mailbox rows: each dead mailbox gets its OWN reconnect
              button that pre-selects its address - so a shared mailbox is
              never left dead because the user reconnected a different one. */}
          <ul className="mt-2 space-y-1.5">
            {dead.map((m) => {
              const shared = isShared(m)
              const isGmail = m.provider === 'gmail'
              return (
                <li
                  key={m.id}
                  className="flex items-center justify-between gap-2 rounded-md bg-red-500/5 px-2 py-1"
                >
                  <div className="min-w-0 flex items-center gap-1.5">
                    <span className="text-xs truncate" title={m.email_address}>
                      {m.email_address || `Mailbox #${m.id}`}
                    </span>
                    {shared && (
                      <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-red-500/15 font-medium flex-shrink-0">
                        Shared
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => reconnect(m)}
                    title={
                      shared
                        ? `Sign in as ${m.email_address} to reconnect this shared mailbox`
                        : `Reconnect ${m.email_address}`
                    }
                    className={`text-xs px-2.5 py-1 rounded-md text-white transition-colors flex items-center gap-1.5 flex-shrink-0 ${
                      isGmail
                        ? 'bg-[#EA4335] hover:bg-[#D33C2D]'
                        : 'bg-[#0078D4] hover:bg-[#006CBE]'
                    }`}
                  >
                    <ExternalLink className="h-3 w-3" />
                    Reconnect {isGmail ? 'Google' : 'Microsoft'}
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="p-1 rounded hover:bg-red-500/10 transition-colors flex-shrink-0"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}
